import type { Form } from '../../payload-types';
import { WEBSITE_PATTERN } from '../../utilities/is-valid-website';
import { cn } from '../../utilities/classname';

const fieldLabels: Record<string, string> = {
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  message: 'Message',
  website: 'Website',
  companyName: 'Company Name',
  industry: 'Industry',
};

/**
 * Field caption with a visible required marker. The asterisk is decorative:
 * it is supplied via CSS (`.required-indicator::after`) on an `aria-hidden`
 * span, so it never becomes part of the field's accessible name. The input's
 * native `required` attribute is what conveys the required state to
 * assistive tech.
 */
const FieldLegend: React.FC<{
  htmlFor: string;
  label: string;
  required?: boolean | null;
  className?: string;
}> = ({ htmlFor, label, required, className }) => (
  <label className={cn('fieldset-legend', className)} htmlFor={htmlFor}>
    {label}
    {required ? (
      <span className="required-indicator text-error ms-0.5" aria-hidden="true" />
    ) : null}
  </label>
);

const FormFieldView: React.FC<{
  id: string;
  field: NonNullable<Form['fields']>[number];
  isPending?: boolean;
  defaultValue?: string;
  layout?: 'contact-panel';
}> = ({
  id,
  field,
  isPending,
  defaultValue,
  layout,
}) => {
    const options = field.options?.filter(Boolean);
    const isContactPanel = layout === 'contact-panel';
    const fieldsetClassName = cn(
      'fieldset',
      isContactPanel && 'min-w-0 gap-1.5 p-0',
      isContactPanel && !field.halfWidth && 'lg:col-span-2',
    );
    const labelClassName = isContactPanel ? 'p-0 text-sm font-medium' : undefined;
    const controlClassName = isContactPanel ? 'bg-base-100 text-base-content' : undefined;
    // DaisyUI's lg size (size-field x 12, 18px text) keeps phones from zooming the field;
    // the default size at lg. Both follow the tenant's --size-field.
    const inputClassName = cn('input w-full', isContactPanel && 'input-lg lg:input-md', controlClassName);

    if (options && options.length > 0) {
      const label = fieldLabels[field.type ?? ''] ?? field.type ?? '';
      return (
        <fieldset className={fieldsetClassName}>
          <FieldLegend htmlFor={id} label={label} required={field.required} className={labelClassName} />
          <select
            className={cn('select w-full', isContactPanel && 'select-lg lg:select-md', controlClassName)}
            id={id}
            required={field.required ?? undefined}
            name={field.type ?? undefined}
            disabled={isPending}
            defaultValue={defaultValue ?? ''}
          >
            <option value="" disabled>
              Select {label.toLowerCase()}
            </option>
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </fieldset>
      );
    }

    switch (field.type) {
      case 'name': return (
        <fieldset className={fieldsetClassName}>
          <FieldLegend htmlFor={id} label="Name" required={field.required} className={labelClassName} />
          <input
            type="text"
            autoComplete="name"
            className={inputClassName}
            id={id}
            required={field.required ?? undefined}
            name={field.type}
            readOnly={isPending}
            defaultValue={defaultValue}
          />
        </fieldset>
      );

      case 'email': return (
        <fieldset className={fieldsetClassName}>
          <FieldLegend htmlFor={id} label="Email" required={field.required} className={labelClassName} />
          <input
            type="email"
            className={inputClassName}
            id={id}
            required={field.required ?? undefined}
            name={field.type}
            readOnly={isPending}
            defaultValue={defaultValue} />
        </fieldset>
      );

      case 'phone': return (
        <fieldset className={fieldsetClassName}>
          <FieldLegend htmlFor={id} label="Phone" required={field.required} className={labelClassName} />
          <input
            type="tel"
            className={inputClassName}
            id={id}
            required={field.required ?? undefined}
            name={field.type}
            readOnly={isPending}
            defaultValue={defaultValue} />
        </fieldset>
      );

      case 'website': return (
        <fieldset className={fieldsetClassName}>
          <FieldLegend htmlFor={id} label="Website" required={field.required} className={labelClassName} />
          <input
            type="text"
            inputMode="url"
            autoComplete="url"
            pattern={WEBSITE_PATTERN}
            title="Enter a website like example.com or https://example.com"
            className={inputClassName}
            id={id}
            required={field.required ?? undefined}
            name={field.type}
            readOnly={isPending}
            defaultValue={defaultValue} />
        </fieldset>
      );

      case 'message': return (
        <fieldset className={fieldsetClassName}>
          <FieldLegend htmlFor={id} label="Message" required={field.required} className={labelClassName} />
          <textarea
            className={cn('textarea w-full', controlClassName, isContactPanel && 'textarea-lg lg:textarea-md min-h-24 resize-y')}
            rows={isContactPanel ? 3 : undefined}
            id={id}
            required={field.required ?? undefined}
            name={field.type}
            readOnly={isPending}
            defaultValue={defaultValue} />
        </fieldset>
      );

      case 'companyName': return (
        <fieldset className={fieldsetClassName}>
          <FieldLegend htmlFor={id} label="Company Name" required={field.required} className={labelClassName} />
          <input
            type="text"
            autoComplete="organization"
            className={inputClassName}
            id={id}
            required={field.required ?? undefined}
            name={field.type}
            readOnly={isPending}
            defaultValue={defaultValue}
          />
        </fieldset>
      );

      case 'industry': return (
        <fieldset className={fieldsetClassName}>
          <FieldLegend htmlFor={id} label="Industry" required={field.required} className={labelClassName} />
          <input
            type="text"
            className={inputClassName}
            id={id}
            required={field.required ?? undefined}
            name={field.type}
            readOnly={isPending}
            defaultValue={defaultValue}
          />
        </fieldset>
      );

    }
  };

export default FormFieldView;
